import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Package, Hash, Barcode, Tag, Store, Calendar,
  Box, TrendingUp, AlertTriangle, CheckCircle2, XCircle, ImageOff
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import api from '../../services/api';

function InfoRow({ icon: Icon, label, value, mono, accent }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '10px 0',
      borderBottom: '1px solid #F1F5F9',
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: '#F0FDFD', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={15} color="#009995" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500, marginBottom: 2 }}>{label}</div>
        <div style={{
          fontSize: 13, fontWeight: 600, color: accent ? '#009995' : '#0F172A',
          fontFamily: mono ? "'JetBrains Mono', 'Fira Code', monospace" : 'inherit',
          wordBreak: 'break-word',
        }}>
          {value}
        </div>
      </div>
    </div>
  );
}

function ExpiryInfo({ days, date }) {
  if (days === undefined || days === null) return null;
  let variant, label, icon;
  if (days <= 0) {
    variant = 'error'; label = 'Просрочен'; icon = <XCircle size={14} />;
  } else if (days <= 3) {
    variant = 'urgent'; label = `${days} дн. — критично`; icon = <AlertTriangle size={14} />;
  } else if (days <= 7) {
    variant = 'warning'; label = `${days} дн. — внимание`; icon = <AlertTriangle size={14} />;
  } else {
    variant = 'success'; label = `${days} дн.`; icon = <CheckCircle2 size={14} />;
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '10px 0',
      borderBottom: '1px solid #F1F5F9',
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: '#F0FDFD', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Calendar size={15} color="#009995" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500, marginBottom: 4 }}>Срок годности (ОСГ)</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {date && (
            <span style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>
              {new Date(date).toLocaleDateString('ru-RU')}
            </span>
          )}
          <Badge variant={variant} style={{ gap: 4 }}>
            {icon} {label}
          </Badge>
        </div>
      </div>
    </div>
  );
}

function ProductImage({ src, name }) {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const hasImage = src && !imgError;

  return (
    <div style={{
      width: '100%',
      aspectRatio: '1 / 1',
      maxWidth: 400,
      borderRadius: 16,
      overflow: 'hidden',
      background: hasImage ? '#F8FAFC' : 'linear-gradient(135deg, #F0FDFD 0%, #E6FAFB 50%, #E0F7F6 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      border: '1px solid #E2E8F0',
    }}>
      {hasImage ? (
        <>
          {!imgLoaded && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#F8FAFC',
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                border: '3px solid rgba(0,206,238,0.15)',
                borderTopColor: '#00CEEE',
                animation: 'spin 0.8s linear infinite',
              }} />
            </div>
          )}
          <img
            src={src}
            alt={name}
            onError={() => setImgError(true)}
            onLoad={() => setImgLoaded(true)}
            style={{
              width: '100%', height: '100%',
              objectFit: 'contain',
              padding: 16,
              opacity: imgLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease',
            }}
          />
        </>
      ) : (
        <div style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 12, padding: 32, textAlign: 'center',
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: 20,
            background: 'linear-gradient(135deg, #00CEEE20, #00999520)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ImageOff size={32} color="#94A3B8" strokeWidth={1.5} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#64748B' }}>Фото отсутствует</div>
            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>Изображение из 1С не загружено</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProductCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/v1/products/${id}`);
        if (res?.data) setProduct(res.data);
      } catch (e) {
        console.error(e);
        setError('Товар не найден');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 80 }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        border: '3px solid rgba(0,206,238,0.15)',
        borderTopColor: '#00CEEE',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 12px',
      }} />
      <span style={{ color: '#94A3B8', fontSize: 13, fontWeight: 500 }}>Загрузка карточки товара...</span>
    </div>
  );

  if (error || !product) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <Button variant="ghost" icon={<ArrowLeft size={15} />} onClick={() => navigate('/products')} size="sm">
          К каталогу
        </Button>
      </div>
      <Card>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16,
            background: '#FEF2F2',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            <XCircle size={24} color="#EF4444" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>Товар не найден</div>
          <div style={{ fontSize: 13, color: '#94A3B8', marginTop: 4 }}>Проверьте ссылку или вернитесь в каталог</div>
        </div>
      </Card>
    </div>
  );

  const hierarchy = [product.department, product.buying_group, product.category_name, product.subcategory, product.product_type].filter(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, animation: 'fadeIn 0.3s ease' }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Button variant="ghost" icon={<ArrowLeft size={15} />} onClick={() => navigate('/products')} size="sm">
          Каталог
        </Button>
        <span style={{ color: '#CBD5E1', fontSize: 12 }}>/</span>
        <span style={{
          fontSize: 13, color: '#64748B', fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          maxWidth: 300,
        }}>
          {product.name}
        </span>
      </div>

      {/* Main content: photo + info */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 400px) 1fr',
        gap: 20,
        alignItems: 'start',
      }}
        className="product-card-grid"
      >
        {/* Left: Photo */}
        <Card padding="16px">
          <ProductImage src={product.image_url} name={product.name} />
        </Card>

        {/* Right: Main Info */}
        <Card>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 14,
                background: 'linear-gradient(135deg, #00CEEE, #009995)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                boxShadow: '0 4px 12px rgba(0,206,238,0.25)',
              }}>
                <Package size={22} color="#fff" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{
                  fontSize: 20, fontWeight: 800, color: '#0F172A',
                  letterSpacing: '-0.02em', lineHeight: 1.3,
                  wordBreak: 'break-word',
                }}>
                  {product.name}
                </h2>
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  <Badge variant={product.is_active ? 'success' : 'error'} dot>
                    {product.is_active ? 'Активен' : 'Неактивен'}
                  </Badge>
                  {product.brand && <Badge variant="primary">{product.brand}</Badge>}
                </div>
              </div>
            </div>
          </div>

          {/* Info rows */}
          <div>
            <InfoRow icon={Hash} label="Код 1С" value={product.source_code} mono />
            <InfoRow icon={Barcode} label="Штрих-код" value={product.barcode} mono accent />
            <InfoRow icon={Tag} label="Артикул" value={product.article} mono />

            {product.lsp > 0 && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '10px 0',
                borderBottom: '1px solid #F1F5F9',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                  background: '#F0FDFD', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <TrendingUp size={15} color="#009995" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500, marginBottom: 2 }}>Цена (LSP)</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#009995' }}>
                    {Number(product.lsp).toLocaleString('ru-RU')} <span style={{ fontSize: 12, fontWeight: 500 }}>сум</span>
                  </div>
                </div>
              </div>
            )}

            <InfoRow icon={Box} label="Остаток на складе" value={
              product.stock_quantity !== undefined && product.stock_quantity !== null
                ? `${product.stock_quantity} ${product.unit || 'шт'}`
                : null
            } />

            <ExpiryInfo days={product.days_until_expiry} date={product.nearest_expiry} />

            {product.store && (
              <InfoRow icon={Store} label="Магазин" value={product.store.name} />
            )}
          </div>
        </Card>
      </div>

      {/* Classification hierarchy card */}
      {hierarchy.length > 0 && (
        <Card title="Классификация 1С">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {hierarchy.map((item, i) => (
              <React.Fragment key={i}>
                <Badge variant={i === 0 ? 'primary' : 'gray'}>{item}</Badge>
                {i < hierarchy.length - 1 && (
                  <span style={{ color: '#CBD5E1', fontSize: 11, display: 'flex', alignItems: 'center' }}>&rarr;</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </Card>
      )}

      {/* Price details */}
      {(product.store_price > 0 || product.price > 0 || product.discount_price) && (
        <Card title="Ценообразование">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 12,
          }}>
            {product.store_price > 0 && (
              <div style={{
                padding: '14px 16px', borderRadius: 12,
                background: '#F8FAFC', border: '1px solid #E2E8F0',
              }}>
                <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500, marginBottom: 4 }}>Цена магазина</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0F172A' }}>
                  {Number(product.store_price).toLocaleString('ru-RU')} <span style={{ fontSize: 11, fontWeight: 500, color: '#94A3B8' }}>сум</span>
                </div>
              </div>
            )}
            {product.price > 0 && (
              <div style={{
                padding: '14px 16px', borderRadius: 12,
                background: '#F8FAFC', border: '1px solid #E2E8F0',
              }}>
                <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500, marginBottom: 4 }}>Базовая цена</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0F172A' }}>
                  {Number(product.price).toLocaleString('ru-RU')} <span style={{ fontSize: 11, fontWeight: 500, color: '#94A3B8' }}>сум</span>
                </div>
              </div>
            )}
            {product.discount_price && (
              <div style={{
                padding: '14px 16px', borderRadius: 12,
                background: '#ECFDF5', border: '1px solid #D1FAE5',
              }}>
                <div style={{ fontSize: 11, color: '#059669', fontWeight: 500, marginBottom: 4 }}>Со скидкой</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#059669' }}>
                  {Number(product.discount_price).toLocaleString('ru-RU')} <span style={{ fontSize: 11, fontWeight: 500 }}>сум</span>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Mobile-responsive styles injected via <style> tag */}
      <style>{`
        .product-card-grid {
          display: grid;
          grid-template-columns: minmax(0, 400px) 1fr;
          gap: 20px;
          align-items: start;
        }
        @media (max-width: 768px) {
          .product-card-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
